import React, { useMemo, useState } from 'react';
import {
  MdArrowBack, MdPhoneInTalk, MdVideocam, MdStorefront, MdLocationOn,
  MdChevronRight, MdAutoAwesome, MdEventAvailable, MdCheckCircle,
} from 'react-icons/md';
import { useApp } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import { classifyTriage } from '../constants/advisorTriage';
import { CHANNELS, nextSlots } from '../constants/appointmentSlots';

// Public TD Direct Investing line — no proprietary data.
const ADVISOR_PHONE = '1-866-233-3234';

const STORE_KEY = 'iva_advisor_appointments';

// Derive a short human topic label from the captured request text.
function deriveTopic(requestContext) {
  const t = (requestContext || '').trim();
  if (!t) return 'General advice';
  const firstLine = t.split('\n')[0].trim();
  if (firstLine.length <= 80) return firstLine;
  return firstLine.slice(0, 77).trimEnd() + '…';
}

// CHANNELS/nextSlots are shared with the in-chat Swipe scheduler card
// (constants/appointmentSlots.js) — real-looking open slots only + instant
// confirmation (mock), a Westpac cue. CHANNELS carries id/label/emoji only
// (presentation-agnostic); map to react-icons locally for this screen's UI.
const CHANNEL_ICONS = { phone: MdPhoneInTalk, video: MdVideocam, branch: MdStorefront };

const BookAppointmentScreen = ({ onNavigate, onBack, requestContext, onChatHandoff }) => {
  const { theme } = useApp() || {};
  const { loadedProfileData } = useProfile() || {};
  const isLight = theme !== 'purple';

  // Style tokens — mirror AdvisorScreen so themes stay consistent.
  const bg       = isLight ? '#f4f6f8' : 'var(--bg-screen)';
  const cardBg   = isLight ? '#fff' : 'rgba(255,255,255,0.05)';
  const cardBdr  = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)';
  const subText  = isLight ? '#666' : 'rgba(160,210,255,0.6)';
  const valClr   = isLight ? '#111' : 'rgba(220,235,255,0.9)';
  const labelClr = isLight ? '#888' : 'rgba(160,210,255,0.5)';
  const teal     = '#448889';

  const topic = useMemo(() => deriveTopic(requestContext), [requestContext]);
  const triage = useMemo(() => classifyTriage(requestContext), [requestContext]);
  const slots = useMemo(() => nextSlots(3), []);

  const [selectedSlot, setSelectedSlot] = useState(slots[0]?.id);
  const [channel, setChannel] = useState('phone');
  const [confirmed, setConfirmed] = useState(null); // { topic, slot, channel } once booked

  const customerKey = loadedProfileData?.name || 'guest';
  const onlineFirst = triage.mode === 'online-first';

  const handleTryOnline = () => {
    const seed = (requestContext || '').trim();
    if (seed && onChatHandoff) onChatHandoff(seed);
    else onNavigate('chat');
  };

  const handleBook = () => {
    const slot = slots.find(s => s.id === selectedSlot) || slots[0];
    const chan = CHANNELS.find(c => c.id === channel);
    const record = {
      topic,
      slot: slot?.label,
      channel: chan?.label,
      bookedAt: new Date().toISOString(),
    };
    try {
      const all = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
      all[customerKey] = record;
      localStorage.setItem(STORE_KEY, JSON.stringify(all));
    } catch { /* localStorage unavailable — confirmation still shows in-session */ }
    setConfirmed(record);
  };

  const OptionCard = ({ Icon, title, sub, href, onClick }) => {
    const inner = (
      <>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: teal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={22} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: valClr }}>{title}</div>
          <div style={{ fontSize: 13, color: subText, marginTop: 2 }}>{sub}</div>
        </div>
        <MdChevronRight size={22} style={{ color: labelClr, flexShrink: 0 }} />
      </>
    );
    const style = {
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
      background: cardBg, border: `1px solid ${cardBdr}`, borderRadius: 14,
      padding: '14px 16px', margin: '0 16px 12px', cursor: 'pointer', boxSizing: 'border-box',
      color: 'inherit', textDecoration: 'none',
    };
    if (href) {
      return <a href={href} style={style}>{inner}</a>;
    }
    return <button type="button" onClick={onClick} style={style}>{inner}</button>;
  };

  // Confirmed view ----------------------------------------------------------
  if (confirmed) {
    return (
      <div className="screen bh-flow" style={{ background: bg }}>
        <header className="bh-flow-hero">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack} aria-label="Back"><MdArrowBack /></button>
            <h1 className="bh-flow-title">You're booked</h1>
          </div>
        </header>
        <div className="bh-flow-body">
          <div style={{ background: cardBg, border: `1px solid ${cardBdr}`, borderRadius: 14, margin: 'var(--space-md) 16px 12px', padding: '20px 18px', textAlign: 'center' }}>
            <MdCheckCircle size={44} color={teal} />
            <div style={{ fontSize: 17, fontWeight: 700, color: valClr, marginTop: 8 }}>
              Appointment confirmed
            </div>
            <div style={{ fontSize: 14, color: subText, marginTop: 6, lineHeight: 1.55 }}>
              {confirmed.channel} · {confirmed.slot}
            </div>
            <div style={{ fontSize: 14, color: valClr, marginTop: 8 }}>
              Topic: {confirmed.topic}
            </div>
          </div>

          {/* Prepare for your appointment — the plan-90 "what to bring" list */}
          <div style={{ background: cardBg, border: `1px solid ${cardBdr}`, borderRadius: 14, margin: '8px 16px 12px', padding: '16px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: labelClr, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Prepare for your appointment
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: valClr, fontSize: 14, lineHeight: 1.7 }}>
              <li>your goals and timeline</li>
              <li>a list of accounts inside and outside TD</li>
              <li>any tax documents you have</li>
            </ul>
          </div>

          <div style={{ padding: '4px 16px 12px' }}>
            <button
              type="button"
              onClick={handleTryOnline}
              style={{
                width: '100%', border: `1px solid ${teal}`, background: 'transparent',
                color: teal, borderRadius: 12, padding: '12px 16px', fontSize: 15,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Or ask IVA about this now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Booking-draft view ------------------------------------------------------
  const PrimaryButton = ({ label, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', border: 'none', background: teal, color: '#fff',
        borderRadius: 12, padding: '14px 16px', fontSize: 15, fontWeight: 700,
        cursor: 'pointer', marginBottom: 10,
      }}
    >
      {label}
    </button>
  );
  const SecondaryButton = ({ label, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', border: `1px solid ${teal}`, background: 'transparent',
        color: teal, borderRadius: 12, padding: '13px 16px', fontSize: 15,
        fontWeight: 600, cursor: 'pointer', marginBottom: 10,
      }}
    >
      {label}
    </button>
  );

  const tryOnlineBtn = (
    <PrimaryButton label="Try to handle this online now" onClick={handleTryOnline} />
  );
  const bookBtn = onlineFirst
    ? <SecondaryButton label="Book the appointment" onClick={handleBook} />
    : <PrimaryButton label="Book the appointment" onClick={handleBook} />;
  const onlineBtn = onlineFirst
    ? tryOnlineBtn
    : <SecondaryButton label="Try to handle this online now" onClick={handleTryOnline} />;

  return (
    <div className="screen bh-flow" style={{ background: bg }}>
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack} aria-label="Back"><MdArrowBack /></button>
          <h1 className="bh-flow-title">Book with a TD advisor</h1>
        </div>
      </header>

      <div className="bh-flow-body">

        {/* Draft appointment — already in progress, never a dead-end link */}
        <div style={{ background: cardBg, border: `1px solid ${cardBdr}`, borderRadius: 14, margin: 'var(--space-md) 16px 12px', padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <MdEventAvailable size={20} color={teal} />
            <span style={{ fontSize: 13, fontWeight: 700, color: teal, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Appointment draft
            </span>
          </div>
          <div style={{ fontSize: 14, color: subText, marginBottom: 4 }}>Topic</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: valClr, marginBottom: 14 }}>{topic}</div>

          {/* Channel choice — Westpac cue (phone / video / branch) */}
          <div style={{ fontSize: 14, color: subText, marginBottom: 8 }}>How would you like to meet?</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {CHANNELS.map(({ id, label }) => {
              const on = channel === id;
              const Icon = CHANNEL_ICONS[id];
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setChannel(id)}
                  style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '10px 4px', borderRadius: 10, cursor: 'pointer',
                    border: `1px solid ${on ? teal : cardBdr}`,
                    background: on ? (isLight ? 'rgba(68,136,137,0.08)' : 'rgba(68,136,137,0.18)') : 'transparent',
                    color: on ? teal : valClr, fontWeight: on ? 700 : 500, fontSize: 13,
                  }}
                >
                  <Icon size={20} color={on ? teal : labelClr} />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Real-looking open slots only */}
          <div style={{ fontSize: 14, color: subText, marginBottom: 8 }}>Next available</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {slots.map(s => {
              const on = selectedSlot === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelectedSlot(s.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', borderRadius: 10, cursor: 'pointer', width: '100%',
                    border: `1px solid ${on ? teal : cardBdr}`,
                    background: on ? (isLight ? 'rgba(68,136,137,0.08)' : 'rgba(68,136,137,0.18)') : 'transparent',
                    color: valClr, fontSize: 14, fontWeight: on ? 700 : 500,
                  }}
                >
                  <span>{s.label}</span>
                  {on && <MdCheckCircle size={18} color={teal} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Online-first choice — the FIRST decision the customer sees.
            Emphasis (primary/secondary) set by the triage default. */}
        <div style={{ background: cardBg, border: `1px solid ${cardBdr}`, borderRadius: 14, margin: '8px 16px 12px', padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <MdAutoAwesome size={20} color={teal} />
            <span style={{ fontSize: 13, fontWeight: 700, color: teal, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Handle it now, or book?
            </span>
          </div>
          <div style={{ fontSize: 14, color: subText, lineHeight: 1.55, marginBottom: 14 }}>
            {onlineFirst
              ? 'I can likely take care of this right now — want me to try, or would you rather book time with an advisor?'
              : 'This one\'s a good fit for a licensed advisor. I can still help with parts of it online if you\'d like.'}
          </div>
          {onlineFirst ? (<>{onlineBtn}{bookBtn}</>) : (<>{bookBtn}{onlineBtn}</>)}
        </div>

        {/* Existing Call / Visit-a-branch options, kept lower down */}
        <div style={{ padding: '0 0 4px' }}>
          <OptionCard
            Icon={MdPhoneInTalk}
            title="Call us"
            sub={ADVISOR_PHONE}
            href={`tel:${ADVISOR_PHONE.replace(/[^0-9]/g, '')}`}
          />
          <OptionCard
            Icon={MdLocationOn}
            title="Visit a branch"
            sub="Find a location near you"
            onClick={() => onNavigate('locations')}
          />
        </div>

        <div style={{ padding: '4px 24px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: subText, lineHeight: 1.5 }}>
            All advice from a licensed advisor is separate from IVA.
          </div>
        </div>

      </div>
    </div>
  );
};

export default BookAppointmentScreen;
